import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { BackgroundService } from "../background.service";
import type { BackgroundItem } from "../backgrounds.interface";
import { catchError, filter, switchMap, take, takeWhile } from "rxjs/operators";
import { forkJoin, merge, of, Subscription, timer } from "rxjs";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import type { DigitalCardProfileReadiness } from "../../digital-cards/digital-cards.interface";
import {
  DELETE_BACKGROUND_FAILURE,
  DELETE_BACKGROUND_SUCCESS,
  DeleteBackgroundAction,
  DeleteBackgroundSuccessAction,
  GetBackgroundsAction,
} from "../backgrounds.actions";
import {
  getBackgroundsList,
  getBackgroundsLoading,
} from "../backgrounds.reducer";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import {
  canvasListMainPhase,
  type CanvasListMainPhase,
} from "../../shared/canvas-list-view";

const POLLING_INTERVAL = 2000;
const PREVIEW_SYNC_MAX_POLLS = 60;

@Component({
  selector: "app-background-list",
  templateUrl: "./background-list.component.html",
  styleUrls: ["./background-list.component.scss"],
  standalone: false,
})
export class BackgroundListComponent implements OnInit, OnDestroy {
  loading = true;
  private listLoading = true;
  deleting = false;
  loadingPreview = false;
  previewLoadError = false;
  previewSyncing = false;
  previewUrl: string | null = null;
  readOnly = false;
  isDownloading = false;
  items: BackgroundItem[] = [];
  selected: BackgroundItem | null = null;
  previewEmployee: EmployeePickerItem | null = null;
  employeePickerReady = false;
  private storeSub?: Subscription;
  private previewSub?: Subscription;
  private previewRequestSeq = 0;

  constructor(
    private store: Store,
    private actions$: Actions,
    private route: ActivatedRoute,
    private router: Router,
    private backgroundService: BackgroundService,
    private digitalCardService: DigitalCardService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.readOnly = !!this.route.snapshot.data["readOnly"];
    this.storeSub = new Subscription();
    this.storeSub.add(
      this.store.select(getBackgroundsLoading).subscribe((listLoading) => {
        this.listLoading = listLoading;
        this.loading =
          this.listLoading ||
          (!this.readOnly && !!this.selected && !this.employeePickerReady);
      }),
    );
    this.storeSub.add(
      this.store.select(getBackgroundsList).subscribe((items) => {
        this.items = items ?? [];
        if (this.items.length === 0) {
          this.selected = null;
          if (this.previewUrl) {
            window.URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = null;
          }
          this.loadingPreview = false;
          this.previewLoadError = false;
          this.previewSyncing = false;
          this.loading =
            this.listLoading ||
            (!this.readOnly && !!this.selected && !this.employeePickerReady);
          return;
        }
        const selectedId = this.selected?.backgroundId ?? "";
        const keep =
          selectedId.length > 0 &&
          this.items.some((item) => (item.backgroundId ?? "") === selectedId);
        if (!keep) {
          this.select(this.items[0]);
        } else if (this.selected) {
          this.selected =
            this.items.find(
              (item) => (item.backgroundId ?? "") === selectedId,
            ) ?? this.items[0];
        }
        this.loading =
          this.listLoading ||
          (!this.readOnly && !!this.selected && !this.employeePickerReady);
      }),
    );
    this.store.dispatch(new GetBackgroundsAction({ mine: this.readOnly }));
  }

  ngOnDestroy(): void {
    this.storeSub?.unsubscribe();
    this.previewSub?.unsubscribe();
    if (this.previewUrl) {
      window.URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  get pickerTemplateContextId(): string {
    return this.readOnly ? "backgrounds-my" : "backgrounds";
  }

  get listBusy(): boolean {
    return this.loading;
  }

  get contentReady(): boolean {
    return !this.previewSyncing && !this.loadingPreview;
  }

  get mainPhase(): CanvasListMainPhase {
    return canvasListMainPhase({
      listBusy: this.listBusy,
      hasSelection: !!this.selected,
      contentReady: this.contentReady,
    });
  }

  get mainReady(): boolean {
    return this.mainPhase === "ready";
  }

  get showPreviewSyncing(): boolean {
    if (!this.selected) {
      return false;
    }
    if (this.previewSyncing) {
      return true;
    }
    return this.loadingPreview && !this.previewUrl;
  }

  select(item: BackgroundItem): void {
    const backgroundId = item.backgroundId?.trim();
    if (!backgroundId) {
      return;
    }
    if ((this.selected?.backgroundId ?? "") === backgroundId) {
      return;
    }
    this.selected = item;
    this.clearPreviewUrl();
    this.loadPreview(backgroundId);
  }

  private clearPreviewUrl(): void {
    if (this.previewUrl) {
      window.URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  onPreviewEmployeeChange(emp: EmployeePickerItem | null): void {
    this.previewEmployee = emp;
    this.employeePickerReady = true;
    this.loading = this.listLoading;
    const backgroundId = this.selected?.backgroundId?.trim();
    if (!backgroundId) return;
    this.loadPreview(backgroundId);
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    this.loading = this.listLoading;
    const backgroundId = this.selected?.backgroundId?.trim();
    if (backgroundId) {
      this.loadPreview(backgroundId);
    }
  }

  isSelected(item: BackgroundItem): boolean {
    return (this.selected?.backgroundId ?? "") === (item.backgroundId ?? "");
  }

  createNew(): void {
    void this.router.navigate(["/backgrounds/create"]);
  }

  editSelected(): void {
    const backgroundId = this.selected?.backgroundId?.trim();
    if (!backgroundId) return;
    void this.router.navigate(["/backgrounds", backgroundId, "edit"]);
  }

  remove(): void {
    const targetId = this.selected?.backgroundId?.trim();
    if (!targetId || this.deleting) return;
    this.deleting = true;
    this.store.dispatch(new DeleteBackgroundAction(targetId));
    const done = merge(
      this.actions$.pipe(
        ofType(DELETE_BACKGROUND_SUCCESS),
        filter(
          (action: DeleteBackgroundSuccessAction) =>
            action.payload === targetId,
        ),
      ),
      this.actions$.pipe(ofType(DELETE_BACKGROUND_FAILURE)),
    )
      .pipe(take(1))
      .subscribe(() => {
        this.deleting = false;
      });
    this.storeSub?.add(done);
  }

  download(): void {
    const backgroundId = this.selected?.backgroundId?.trim();
    if (!backgroundId || this.isDownloading) return;
    this.isDownloading = true;
    const request$ = this.readOnly
      ? this.backgroundService.downloadImage(backgroundId, true)
      : this.backgroundService.downloadZip(backgroundId, false);
    request$.subscribe({
      next: (blob) => {
        this.isDownloading = false;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = this.readOnly
          ? `background-${backgroundId}.png`
          : "videocall-backgrounds.zip";
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.isDownloading = false;
        this.toastr.error(
          this.translate.instant(
            this.readOnly
              ? "digitalCards.downloadBackgroundFailed"
              : "digitalCards.downloadBackgroundsZipFailed",
          ) as string,
        );
      },
    });
  }

  onPreviewLoad(): void {
    this.previewLoadError = false;
    this.loadingPreview = false;
  }

  onPreviewError(): void {
    this.loadingPreview = false;
    this.previewLoadError = true;
    const backgroundId = this.selected?.backgroundId?.trim();
    const templateId = this.selected?.digitalCardProfileId?.trim() || "";
    if (backgroundId && templateId && !this.readOnly) {
      this.startPreviewSync(backgroundId);
      return;
    }
    this.previewSyncing = false;
  }

  private loadPreview(backgroundId: string): void {
    if (!backgroundId) return;
    if (!this.readOnly && !this.employeePickerReady) {
      this.clearPreviewUrl();
      this.loadingPreview = true;
      this.previewLoadError = false;
      this.previewSyncing = false;
      return;
    }
    this.previewSub?.unsubscribe();
    this.previewRequestSeq += 1;
    const requestSeq = this.previewRequestSeq;
    this.clearPreviewUrl();
    const templateId = this.selected?.digitalCardProfileId?.trim() || "";

    if (templateId && !this.readOnly) {
      this.startPreviewSync(backgroundId, requestSeq);
      return;
    }

    this.loadingPreview = true;
    this.previewLoadError = false;
    this.previewSyncing = false;
    const previewEmail = this.previewEmployee?.email?.trim() || null;
    this.previewSub = this.backgroundService
      .downloadImage(backgroundId, this.readOnly, previewEmail)
      .subscribe({
        next: (blob) => {
          if (requestSeq !== this.previewRequestSeq) return;
          this.applyPreviewBlob(blob);
        },
        error: () => {
          if (requestSeq !== this.previewRequestSeq) return;
          this.clearPreviewUrl();
          this.loadingPreview = false;
          this.previewLoadError = true;
          this.previewSyncing = false;
        },
      });
  }

  private startPreviewSync(
    backgroundId: string,
    requestSeq = ++this.previewRequestSeq,
  ): void {
    const templateId = this.selected?.digitalCardProfileId?.trim() || "";
    if (!templateId) {
      return;
    }
    this.previewSub?.unsubscribe();
    this.previewRequestSeq = requestSeq;
    this.clearPreviewUrl();
    this.loadingPreview = false;
    this.previewLoadError = false;
    this.previewSyncing = true;
    const previewEmail = this.previewEmployee?.email?.trim() || null;
    let polls = 0;

    this.previewSub = timer(0, POLLING_INTERVAL)
      .pipe(
        switchMap(() =>
          forkJoin({
            readiness: this.digitalCardService
              .getProfileReadiness(templateId, false)
              .pipe(
                catchError(() =>
                  of<DigitalCardProfileReadiness>({ status: "pending" }),
                ),
              ),
            image: this.backgroundService
              .downloadImage(backgroundId, this.readOnly, previewEmail)
              .pipe(catchError(() => of(null))),
          }),
        ),
        takeWhile((result) => {
          if (requestSeq !== this.previewRequestSeq) {
            return false;
          }
          polls += 1;
          if (polls > PREVIEW_SYNC_MAX_POLLS) {
            return false;
          }
          if (result.image && result.readiness.status === "ready") {
            return false;
          }
          if (result.readiness.status === "ready" && !result.image) {
            return false;
          }
          return true;
        }, true),
      )
      .subscribe({
        next: (result) => {
          if (requestSeq !== this.previewRequestSeq) return;
          if (result.image && result.readiness.status === "ready") {
            this.applyPreviewBlob(result.image);
            return;
          }
          if (result.readiness.status === "ready" && !result.image) {
            this.previewSyncing = false;
            this.previewLoadError = true;
            this.loadingPreview = false;
          }
        },
        complete: () => {
          if (requestSeq !== this.previewRequestSeq) return;
          if (!this.previewUrl) {
            this.previewSyncing = false;
            if (!this.previewLoadError) {
              this.previewLoadError = true;
            }
            this.loadingPreview = false;
          }
        },
      });
  }

  private applyPreviewBlob(blob: Blob): void {
    this.clearPreviewUrl();
    this.previewUrl = window.URL.createObjectURL(blob);
    this.previewLoadError = false;
    this.previewSyncing = false;
    this.loadingPreview = true;
  }
}
